import React, { useMemo } from 'react';
const shortHandHexRegExp = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
const fullHexRegExp = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
/**
 * Utilize a small cache for the calculated icon colors. This is useful if there are many icons
 * with the same color (typical use case) so we don't need to run the calculations per component instance.
 */
const cache = new Map();
/**
 * Determines a SVG filter to re-color an image. Returns the css filter value and
 * a component containing the SVG filter definitions.
 *
 * @example
 * const { component, filter } = useColorFilter({ color: '#FFFFFF' })
 *
 * return (
 *  <>
 *   {component}
 *   <img src="..." style={{ filter }} />
 *  </>
 * )
 *
 *
 */
export const useColorFilter = ({ color, id = [], }) => {
    const filterId = useMemo(() => [...id, color].filter(Boolean).join('-'), [id, color]);
    if (!color) {
        return {
            filter: undefined,
            component: null,
        };
    }
    return {
        filter: `url(#${filterId})`,
        component: React.createElement(SVGColorMatrixFilterDefinition, { id: filterId, targetColor: color }),
    };
};
/**
 * SVG filter definitions for recoloring an image. Calculates the filter matrix
 * internally and caches the result.
 */
const SVGColorMatrixFilterDefinition = ({ id, targetColor }) => {
    const filters = useMemo(() => calculateColorTransformMatrix(targetColor), [targetColor]);
    return (React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", version: "1.1", width: "0", height: "0", "aria-hidden": true, 
        /* Hide the element from e.g., flex layouts with collapse. Also make it inline for non-flex layouts */
        style: { visibility: 'collapse', display: 'inline' } },
        React.createElement("defs", null,
            React.createElement("filter", { id: id, colorInterpolationFilters: "sRGB" },
                React.createElement("feColorMatrix", { type: "matrix", values: filters })))));
};
function calculateColorTransformMatrix(target) {
    if (cache.has(target)) {
        return cache.get(target);
    }
    const rgb = hexToRgb(target);
    if (!rgb) {
        throw new Error(`Invalid target color ${target}`);
    }
    const [r, g, b] = rgb.map((v) => v / 255);
    const result = `
  0 0 0 0 ${r}
  0 0 0 0 ${g}
  0 0 0 0 ${b}
  0 0 0 1 0
`;
    cache.set(target, result);
    return result;
}
function hexToRgb(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const expandedHex = hex.replace(shortHandHexRegExp, (_, r, g, b) => r + r + g + g + b + b);
    const result = fullHexRegExp.exec(expandedHex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : undefined;
}
